import { Box, Link, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useNavigate } from "react-router-dom";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { trackEvent } from "utils/tracking";

type QA = { question: string; answer: string | JSX.Element };

export default function AccordionUsage({
  planName,
  boughtTemplates,
}: {
  planName?: Plans;
  boughtTemplates: boolean;
}) {
  const navigate = useNavigate();
  const trackFaqClick = (qa: QA) => {
    trackEvent("VA Onboarding Flow - FAQ Clicked", {
      question: qa.question,
    });
  };

  const goToVaDashboard = () => {
    trackEvent("VA Onboarding Flow - Go to VA Dashboard Clicked");
    navigate(`/${routes.virtualAssistant}`);
  };

  const QuestionsAndAnswers = (planName?: Plans) => {
    const questions: QA[] = [];
    const boughtTemplatesFaq = [
      {
        question: "Do I approve the pitches before my VA begins pitching?",
        answer:
          "Yes! You will receive an email to review your pitches in your dashboard. No pitches will be sent out until you approve them.",
      },
      {
        question: "Can I add or change my pitches later on?",
        answer:
          "Yes! You can edit your pitches in your dashboard, and any new pitches that go out will use your most up-to-date pitch.",
      },
    ];
    const defaultFaq = [
      {
        question: `Can I add a new niche later on?`,
        answer: (
          <>
            Yes! You can add a new niche later on in your{" "}
            <Link sx={{ cursor: "pointer" }} onClick={goToVaDashboard}>
              Virtual Assistant dashboard
            </Link>
          </>
        ),
      },
      {
        question: "What if my VA is pitching to brands I don't like?",
        answer: (
          <>
            We use AI to decide which brands to pitch to on your behalf. You
            will receive a link to download our app, where you will be notified
            a few days before pitches go out. You can review the list of brands
            and delete any you don't want to be pitched to. Alternatively, if
            you'd like to be more hands-on, you can favorite the brands you'd
            like to be pitched to, and we will prioritize sending pitches to
            those brands. Once we've gone through your entire list, we will
            begin preselecting brands for you.
            <br />
            <strong>IMPORTANT:</strong> If you like to give feedback on why a
            brand is not a fit for you, please email us at{" "}
            <a href="mailto:feedback@onbento.com">feedback@onbento.com</a>.
          </>
        ),
      },
      {
        question: "Do pitches go out through my email?",
        answer:
          "Yes, emails will be sent from the email linked to your Bento account.",
      },
      {
        question:
          "How do I share successful deals I've received through the Virtual Assistant?",
        answer: (
          <>
            We'd love to hear about your successful deals! DM us on Instagram{" "}
            <a href="https://www.instagram.com/onbento/">(@onbento)</a> or email
            us at <a href="mailto:va@onbento.com">va@onbento.com</a>
          </>
        ),
      },
      {
        question: "Will Bento help me negotiate deals with brand?",
        answer: (
          <>
            Yes, we do! Here's how it works:
            <ul>
              <li>
                Forward us any brand negotiation you're in the middle of to{" "}
                <a href="mailto:negotiations@onbento.com">
                  negotiations@onbento.com
                </a>{" "}
                (currently, unlimited uses!!)
              </li>
              <li>
                Our system scans both the internet and Bento's database for pay
                data relevant to your deal.
              </li>
              <li>
                Within <strong>1 day</strong>, our customer success team will
                send you tailored insights plus a pre-drafted email you can use
                to strengthen your negotiation and secure higher rates.
              </li>
              <li>
                A few things to know:
                <ul>
                  <li>
                    We'll only provide pay data and insights for serious brand
                    conversations (to ensure you're getting high-quality,
                    actionable support).
                  </li>
                  <li>
                    The goal: save you time, stop lowballing, and help you get
                    paid what you're worth.
                  </li>
                </ul>
              </li>
            </ul>
          </>
        ),
      },
    ];
    if (boughtTemplates) {
      questions.push(...boughtTemplatesFaq);
    }
    questions.push(...defaultFaq);
    return questions;
  };

  return (
    <Box sx={{ mt: 3 }}>
      {QuestionsAndAnswers(planName).map((qa, index) => (
        <Accordion
          key={index}
          elevation={0}
          style={{ boxShadow: "none" }}
          sx={{ backgroundColor: "secondary.light" }}
          onClick={() => trackFaqClick(qa)}
        >
          <AccordionSummary
            expandIcon={
              <Box component="i" className="fa-regular fa-chevron-down" />
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            {qa.question}
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">{qa.answer}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
